import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AlarmOffIcon from "@mui/icons-material/AlarmOff";
import { SlaBar } from "../../shared/ui/charts";
import { businessMinutesBetween, formatMinutes, PackageSla, SLA_TARGETS, STAGE_LABEL } from "../packages";
import ReasonDialog from "./ReasonDialog";
import ReasonPicker from "./ReasonPicker";
import { useReasonCapture } from "./useReasonCapture";
import { SlaRequirement } from "./errors";
import { ReasonCapture } from "./types";

export type SlaJustifyDialogProps = {
  requirement: SlaRequirement;
  sla?: PackageSla | null;
  context?: string;
  /** Rótulo do destino no botão ("Assinatura", "Antecipe", "Antecipado"…). */
  targetLabel: string;
  direction: "forward" | "back";
  busy: boolean;
  error?: string | null;
  onConfirm: (sla: ReasonCapture) => void;
  onClose: () => void;
  now?: Date;
};

const pad = (n: number) => String(n).padStart(2, "0");
/** "08/10 09:00" a partir de um DATETIME do BE (sem fuso = horário de Brasília, como o BE grava). */
const enteredLabel = (iso: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(iso);
  if (m && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso)) return `${m[3]}/${m[2]} ${m[4]}:${m[5]}`;
  const d = new Date(Date.parse(iso) - 3 * 3_600_000);
  return Number.isNaN(d.getTime()) ? "—" : `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
};

function Cell({ label, children, danger = false }: { label: string; children: string; danger?: boolean }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{label}</Typography>
      <Typography sx={{ fontSize: 14, fontWeight: danger ? 700 : 600, color: danger ? "error.main" : "text.primary" }}>{children}</Typography>
    </Box>
  );
}

/**
 * Janela (c): justificar o atraso da etapa ao sair dela (CA-F1a-05/14). Funciona só com o 422
 * (`extra.stage`/`overMinutes`); com o bloco `sla` da linha (BE-1g) mostra também a entrada na etapa e o corte útil.
 */
export default function SlaJustifyDialog({ requirement, sla, context, targetLabel, direction, busy, error, onConfirm, onClose, now }: SlaJustifyDialogProps) {
  const capture = useReasonCapture("sla_breach");
  const stage = requirement.stage;
  const { targetMin, challengeMin } = sla && sla.stage === stage ? sla : SLA_TARGETS[stage];
  const elapsed = sla && sla.stage === stage ? sla.elapsedMin : targetMin + requirement.overMinutes;
  const over = Math.max(0, elapsed - targetMin);
  const businessOver = sla ? Math.max(0, businessMinutesBetween(sla.clockStartedAt, now ?? new Date()) - targetMin) : null;
  const back = direction === "back";

  return (
    <ReasonDialog
      title={back ? "Justificar atraso para devolver" : "Justificar atraso para avançar"}
      context={context}
      confirmLabel={`Justificar e ${back ? "devolver" : "avançar"} para ${targetLabel}`}
      confirmIcon={back ? <ArrowBackIcon /> : <ArrowForwardIcon />}
      busy={busy}
      error={error}
      valid={capture.valid}
      onConfirm={() => capture.valid && onConfirm(capture.toCapture())}
      onClose={onClose}
    >
      <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px" }} data-testid="sla-summary">
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 1.5, mb: 1.5 }}>
          <Cell label="Etapa atrasada">{STAGE_LABEL[stage]}</Cell>
          {sla ? <Cell label="Entrou na etapa">{enteredLabel(sla.clockStartedAt)}</Cell> : <Box />}
          <Cell label="Meta · meta desafio">{`${formatMinutes(targetMin)} · ${formatMinutes(challengeMin)}`}</Cell>
          <Cell label="Tempo na etapa" danger>
            {formatMinutes(elapsed)}
          </Cell>
        </Box>
        <SlaBar elapsedMin={elapsed} challengeMin={challengeMin} targetMin={targetMin} />
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
          <Chip size="small" color="error" variant="outlined" icon={<AlarmOffIcon />} label={`Estourou a meta em ${formatMinutes(over)}`} sx={{ bgcolor: "#fdecec" }} />
          {businessOver !== null && (
            <Chip size="small" variant="outlined" label={`Horas corridas, sem pausa · no corte útil: ${formatMinutes(businessOver)} acima`} />
          )}
        </Box>
      </Paper>
      <ReasonPicker capture={capture} />
      {stage === "company" && (
        <Alert severity="info">
          Esta justificativa fecha o atraso; se a etapa nunca for concluída, o pacote vira <b>Expirado</b>.
        </Alert>
      )}
    </ReasonDialog>
  );
}
